// src/cockpit4/StatusBar4.tsx
// ============================================
// Cockpit 4.0 底部状态栏
//
// 显示实时系统信息（VSCode 风格底部状态条）：
//   - Hermes 状态（在线/离线/思考中）
//   - DeepSeek 连接（已连接/降级/离线）
//   - Runtime 数量（已注册 / Ready）
//   - 知识库状态（卡片数 / Vault 路径）
//   - Token 消耗（今日已花费 / 预算，超预算时告警）
//   - 当前时间（每秒刷新）
//
// 数据源：RuntimeManager.getStatusSummary() + TokenBudgetManager.getStatus()
// ============================================

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  Cpu,
  Boxes,
  BookMarked,
  Clock,
  Coins,
} from "lucide-react";
import RuntimeManager from "../runtime/RuntimeManager";
import { TokenBudgetManager } from "../runtime/shared/TokenBudgetManager";
// Phase3 P0-4：对业务员工简化底部状态栏（隐藏技术标签）
import { useUserStore } from "../stores/userStore";

// ============================================
// 状态数据类型
// ============================================

interface StatusData {
  runtimeCount: number;
  readyCount: number;
  initialized: boolean;
}

interface TokenStatusData {
  /** 每日预算（元） */
  dailyBudget: number;
  /** 今日已花费（元） */
  spentToday: number;
  /** 使用率 0-1 */
  usageRate: number;
  /** 是否超预算 */
  overBudget: boolean;
  /** 今日累计调用次数 */
  callCountToday: number;
}

// ============================================
// 主组件
// ============================================

export default function StatusBar4() {
  const [now, setNow] = useState(() => new Date());
  const [status, setStatus] = useState<StatusData>({
    runtimeCount: 0,
    readyCount: 0,
    initialized: false,
  });
  const [tokenStatus, setTokenStatus] = useState<TokenStatusData | null>(null);

  // Phase3 P0-4：业务员工只看业务态（系统正常 + 时间），管理员看全技术状态。
  // 判定口径：非 super_admin 且非 guest 的角色（employee 等）一律折叠技术标签。
  const role = useUserStore((s) => s.currentUser?.role);
  const isBizEmployee = role && role !== "super_admin" && role !== "guest";

  // 1) 每秒更新时间
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 2) 每 5 秒刷新 Runtime 状态 + Token 预算状态
  useEffect(() => {
    const budgetManager = TokenBudgetManager.getInstance();

    const refresh = () => {
      try {
        const summary = RuntimeManager.getStatusSummary();
        setStatus({
          runtimeCount: summary.runtimeCount,
          readyCount: summary.readyCount,
          initialized: summary.initialized,
        });
      } catch (err) {
        // 降级保持上一次状态
        console.warn("[StatusBar4] 状态拉取失败", err);
      }

      try {
        const budget = budgetManager.getStatus();
        setTokenStatus({
          dailyBudget: budget.dailyBudget,
          spentToday: budget.spentToday,
          usageRate: budget.usageRate,
          overBudget: budget.overBudget,
          callCountToday: budget.callCountToday,
        });
      } catch (err) {
        console.warn("[StatusBar4] Token 状态拉取失败", err);
      }
    };
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => clearInterval(timer);
  }, []);

  const timeStr = now.toLocaleTimeString("zh-CN", { hour12: false });
  const dateStr = now.toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  // Token 消耗显示文本与颜色（按使用率分级）
  const tokenDisplay = (() => {
    if (!tokenStatus) {
      return { text: "—", color: "text-gray-400", dot: "#6B7280" };
    }
    const spent = tokenStatus.spentToday.toFixed(2);
    const budget = tokenStatus.dailyBudget.toFixed(0);
    const text = `¥${spent} / ¥${budget}`;
    if (tokenStatus.overBudget) {
      return { text, color: "text-red-300", dot: "#EF4444" };
    }
    if (tokenStatus.usageRate >= 0.8) {
      return { text, color: "text-amber-300", dot: "#F59E0B" };
    }
    if (tokenStatus.usageRate >= 0.5) {
      return { text, color: "text-yellow-200", dot: "#EAB308" };
    }
    return { text, color: "text-emerald-300", dot: "#10B981" };
  })();

  return (
    <footer className="relative flex items-center justify-between h-7 px-3 border-t border-white/[0.06] bg-white/[0.02] backdrop-blur-xl text-[10px] font-mono">
      {isBizEmployee ? (
        /* ============ 业务员工简化态：只显示业务友好状态 + 时间 ============ */
        <>
          <div className="flex items-center gap-2">
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-emerald-400"
              animate={{ opacity: [1, 0.4, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-gray-500 uppercase tracking-wider">系统正常</span>
            <span className="text-gray-700">·</span>
            <span className="text-emerald-300">{timeStr}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-500">{dateStr}</span>
            <span className="text-gray-600">·</span>
            <span className="text-gray-500 uppercase tracking-wider">XEAOS · Cockpit 4.0</span>
          </div>
        </>
      ) : (
        <>
        {/* ============ 管理员态：左侧 Hermes + DeepSeek + Runtime + Token ============ */}
        <div className="flex items-center gap-4">
          {/* Hermes 状态 */}
          <StatusItem
            icon={<Sparkles size={11} className="text-purple-400" />}
            label="Hermes"
            value="Online"
            valueColor="text-purple-300"
            dot="#A855F7"
          />

          {/* DeepSeek 连接 */}
          <StatusItem
            icon={<Cpu size={11} className="text-emerald-400" />}
            label="DeepSeek"
            value="Connected"
            valueColor="text-emerald-300"
            dot="#10B981"
          />

          {/* Runtime 数量 */}
          <StatusItem
            icon={<Boxes size={11} className="text-blue-400" />}
            label="Runtimes"
            value={`${status.readyCount}/${status.runtimeCount}`}
            valueColor="text-blue-300"
            dot={status.initialized ? "#3B82F6" : "#F59E0B"}
          />

          {/* 知识库状态 */}
          <StatusItem
            icon={<BookMarked size={11} className="text-cyan-400" />}
            label="Knowledge"
            value="Vault · Ready"
            valueColor="text-cyan-300"
            dot="#06B6D4"
          />

          {/* Token 消耗（今日已花费 / 预算） */}
          <StatusItem
            icon={<Coins size={11} className="text-yellow-400" />}
            label="Token"
            value={tokenDisplay.text}
            valueColor={tokenDisplay.color}
            dot={tokenDisplay.dot}
          />
        </div>

        {/* ============ 右侧：日期 + 时间 ============ */}
        <div className="flex items-center gap-3">
          <span className="text-gray-500">{dateStr}</span>
          <span className="text-gray-600">·</span>
          <motion.span
            key={timeStr}
            initial={{ opacity: 0.5 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-1 text-white"
          >
            <Clock size={11} className="text-gray-400" />
            {timeStr}
          </motion.span>
          <span className="text-gray-600">·</span>
          <span className="text-gray-500 uppercase tracking-wider">
            XEAOS · Cockpit 4.0
          </span>
        </div>
        </>
      )}
    </footer>
  );
}

// ============================================
// 子组件：单个状态项
// ============================================

interface StatusItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  valueColor?: string;
  dot?: string;
}

function StatusItem({
  icon,
  label,
  value,
  valueColor = "text-gray-300",
  dot,
}: StatusItemProps) {
  return (
    <div className="flex items-center gap-1.5">
      {dot && (
        <motion.span
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: dot, boxShadow: `0 0 6px ${dot}` }}
          animate={{ opacity: [1, 0.5, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      )}
      {icon}
      <span className="text-gray-500 uppercase tracking-wider">{label}</span>
      <span className={valueColor}>{value}</span>
    </div>
  );
}
